package fr.labodoc.healthProfessional.webApp.pages.learnedSocieties

import fr.labodoc.healthProfessional.webApp.data.repository.LearnedSocietiesRepository
import fr.labodoc.healthProfessional.webApp.data.repository.MedicalProfessionsRepository
import io.kvision.core.StringPair
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class LearnedSocietiesPageViewModelImpl(
  private val learnedSocietyRepository: LearnedSocietiesRepository,
  private val medicalProfessionRepository: MedicalProfessionsRepository,
  private val healthProfessionalMedicalSpecialityId: String?,
  private val viewModelScope: CoroutineScope
) : LearnedSocietiesPageViewModel() {
  private val learnedSocieties: MutableList<LearnedSociety> = mutableListOf()

  override val filter: MutableStateFlow<Filter> = MutableStateFlow(Filter.MySpeciality)
  override val medicalSpecialityFilterOptions: MutableStateFlow<Map<String?, List<StringPair>>> by lazy {
    refreshMedicalSpecialityFilterOptions()
    MutableStateFlow(emptyMap())
  }

  override val learnedSocietiesListState: MutableStateFlow<LearnedSocietiesList> by lazy {
    refreshLearnedSocieties()
    MutableStateFlow(LearnedSocietiesList.Loading)
  }

  override val events: MutableSharedFlow<Event> = MutableSharedFlow()

  override fun refreshMedicalSpecialityFilterOptions() {
    viewModelScope.launch {
      medicalSpecialityFilterOptions.value = emptyMap()

      medicalSpecialityFilterOptions.value = medicalProfessionRepository.getMedicalProfessions()
        .fold(
          {
            emptyMap()
          },
          { response ->
            response
              .associate { medicalProfession ->
                medicalProfession.name to medicalProfession.medicalSpecialities.map { it.id to it.name }
              }
          }
        )
    }
  }

  override fun refreshLearnedSocieties() {
    viewModelScope.launch {
      learnedSocietiesListState.value = LearnedSocietiesList.Loading
      learnedSocieties.clear()

      learnedSocietiesListState.value = learnedSocietyRepository.getLearnedSocieties()
        .fold(
          {
            LearnedSocietiesList.Error
          },
          { response ->
            learnedSocieties.addAll(
              response
                .map { learnedSociety ->
                  LearnedSociety(
                    id = learnedSociety.id,
                    name = learnedSociety.name,
                    websiteUrl = learnedSociety.websiteUrl,
                    logoUrl = learnedSociety.logoUrl,
                    favorite = learnedSociety.favorite,
                    forExterns = learnedSociety.forStudents,
                    medicalSpecialityIds = learnedSociety.medicalSpecialityIds
                  )
                }
                .sortedBy(LearnedSociety::name)
            )

            LearnedSocietiesList.Loaded(learnedSocieties.filter(filter.value))
          }
        )
    }
  }

  override fun updateFilter(filter: Filter) {
    this@LearnedSocietiesPageViewModelImpl.filter.value = filter

    learnedSocietiesListState.update { currentLearnedSocietiesListState ->
      when (currentLearnedSocietiesListState) {
        is LearnedSocietiesList.Loaded -> {
          currentLearnedSocietiesListState.copy(
            learnedSocieties = learnedSocieties.filter(filter)
          )
        }

        else -> currentLearnedSocietiesListState
      }
    }
  }

  override fun updateFavoriteStatus(learnedSociety: LearnedSociety) {
    viewModelScope.launch {
      val response = if (learnedSociety.favorite)
        learnedSocietyRepository.unfavorite(learnedSociety.id)
      else
        learnedSocietyRepository.favorite(learnedSociety.id)

      response
        .onLeft {
          events.emit(Event.UpdateFavoriteStatusFailed(learnedSociety))
        }
        .onRight {
          learnedSocietiesListState.update { currentLearnedSocietiesListState ->
            when (currentLearnedSocietiesListState) {
              is LearnedSocietiesList.Loaded -> {
                currentLearnedSocietiesListState.copy(
                  learnedSocieties = currentLearnedSocietiesListState.learnedSocieties
                    .map {
                      if (it.id == learnedSociety.id)
                        it.copy(favorite = !learnedSociety.favorite)
                      else
                        it
                    }
                )
              }

              else -> currentLearnedSocietiesListState
            }
          }
        }
    }
  }

  private fun List<LearnedSociety>.filter(filter: Filter): List<LearnedSociety> {
    return when (filter) {
      is Filter.All -> this
      is Filter.Favorites -> this.filter(LearnedSociety::favorite)
      is Filter.MySpeciality -> healthProfessionalMedicalSpecialityId?.let { this.filter { healthProfessionalMedicalSpecialityId in it.medicalSpecialityIds } } ?: this.filter(LearnedSociety::forExterns)
      is Filter.MedicalSpecialities -> this.filter { it.medicalSpecialityIds.any { it in filter.medicalSpecialityIds } }
    }
  }
}
